import type {
  RecruitmentPageHeading, RecruitmentPageContent, RecruitmentEntryButton,
  ScoutMessage, Recruitments, Stories, RecruitmentBrands, Hashtags, OccupationItems,
  OtherOccupationItems, IsNotFulfilledProfiled
} from '../types/recruitmentShow';
import type {
  Recruitment,
  SearchFormOptions,
  RecruitmentSearchRequest,
  RecruitmentSearchResponse,
  SimilarRecruitmentSearchRequest,
  SimilarRecruitmentSearchResponse,
  FetchRecruitmentsResponse,
  UserPreferencesResponse,
  SearchHistories,
  SaveConditionsResponse
} from '../types/recruitments';
import { wrapFetch } from '../libs/fetch';
import { IsSupported, Supporters } from '../types/support';

export const showRecruitment = async (id: string | number) => {
  return await wrapFetch<Recruitment>(`/api/v1/recruitments/show_recruitment/?id=${id}`);
};

export const showRecruitmentPageHeading = async (id: number) => {
  return await wrapFetch<RecruitmentPageHeading>(`/api/v1/recruitments/show_recruitment_page_heading/?id=${id}`);
};

export const showRecruitmentPageContent = async (id: number) => {
  return await wrapFetch<RecruitmentPageContent>(`/api/v1/recruitments/show_recruitment_page_content/?id=${id}`);
};

export const fetchRecruitmentEntryButton = async (id: number) => {
  return await wrapFetch<RecruitmentEntryButton>(`/api/v1/recruitments/fetch_entry_button/?id=${id}`);
};

export const fetchScoutMessage = async (id: number) => {
  return await wrapFetch<ScoutMessage>(`/api/v1/recruitments/get_scout_message/?id=${id}`);
};

export const showRecruitmentPageRecruitments = async (id: number) => {
  return await wrapFetch<Recruitments>(`/api/v1/recruitments/show_recruitment_page_recruitments/?id=${id}`);
};

export const showRecruitmentPageRecruitmentsForOccupations = async (id: number) => {
  return await wrapFetch<Recruitments>(`/api/v1/recruitments/show_recruitment_page_recruitments_for_occupations/?id=${id}`);
};

export const showRecruitmentPageStories = async (id: number) => {
  return await wrapFetch<Stories>(`/api/v1/recruitments/show_recruitment_page_stories/?id=${id}`);
};

export const showRecruitmentPageBrands = async (id: number) => {
  return await wrapFetch<RecruitmentBrands>(`/api/v1/recruitments/show_recruitment_page_brands/?id=${id}`);
};

export const showRecruitmentPageRelatedRecruitments = async (id: number) => {
  return await wrapFetch<Recruitments>(`/api/v1/recruitments/show_recruitment_page_related_recruitments/?id=${id}`);
};

export const showRecruitmentPageBrowsingRecruitments = async (id: number) => {
  return await wrapFetch<Recruitments>(`/api/v1/recruitments/show_recruitment_page_browsing_recruitments/?id=${id}`);
};

export const showRecruitmentPageHashtags = async (id: number) => {
  return await wrapFetch<Hashtags>(`/api/v1/recruitments/show_recruitment_page_hashtags/?id=${id}`);
};

export const showRecruitmentPageOccupations = async (id: number) => {
  return await wrapFetch<OccupationItems>(`/api/v1/recruitments/show_recruitment_page_occupations/?id=${id}`);
};

export const showRecruitmentPageOthers = async (id: number) => {
  return await wrapFetch<OtherOccupationItems>(`/api/v1/recruitments/show_recruitment_page_others/?id=${id}`);
};

export const getSearchFormOptions = async () => {
  return await wrapFetch<SearchFormOptions>('/api/v1/recruitments/get_search_form_options');
};

export const getUser = async () => {
  return await wrapFetch<{ user: string }>('/api/v1/recruitments/get_user');
};

export const searchRecruitments = async ({ csrfToken, data }: { csrfToken: string; data: RecruitmentSearchRequest }) => {

  return await wrapFetch<RecruitmentSearchResponse>('/api/v1/recruitments/search_recruitments', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(data),
    credentials: 'same-origin'
  });
};

export const searchSimilarRecruitments = async ({
  csrfToken,
  data
}: {
  csrfToken: string;
  data: SimilarRecruitmentSearchRequest;
}) => {
  return await wrapFetch<SimilarRecruitmentSearchResponse>('/api/v1/recruitments/similar_recruitments', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(data),
    credentials: 'same-origin'
  });
};

export const fetchRecruitments = async () => {
  return await wrapFetch<FetchRecruitmentsResponse>('/api/v1/recruitments/fetch_recruitments');
};

export const getUserPreferences = async () => {
  return await wrapFetch<UserPreferencesResponse>('/api/v1/people/get_person_prefectures_occupations');
};

export const getRecruitmentSupporters = async (recruitmentId: number) => {
  return await wrapFetch<Supporters>(`/api/v1/recruitments/${recruitmentId}/supporters`);
};

export const getRecruitmentIsSupported = async (recruitmentId: number) => {
  return await wrapFetch<IsSupported>(`/api/v1/recruitments/${recruitmentId}/is_supported`);
};

export const isNotFulfilledProfiledForRecruitment = async (recruitmentId: number) => {
  return await wrapFetch<IsNotFulfilledProfiled>(`/api/v1/recruitments/${recruitmentId}/is_not_fulfilled_profiled_for_recruitment`);
};

export const getSearchHistories = async () => {
  return await wrapFetch<SearchHistories>('/api/v1/recruitments/get_search_conditions');
}

export const saveSearchConditions = async ({ csrfToken, data }: { csrfToken: string; data: RecruitmentSearchRequest }) => {

  return await wrapFetch<SearchHistories>('/api/v1/recruitments/save_search_conditions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(data),
    credentials: 'same-origin'
  });
};

export const getSaveConditions = async () => {
  return await wrapFetch<SaveConditionsResponse>('/api/v1/recruitments/get_save_conditions');
}

export const deleteSaveConditions = async ({ csrfToken, conditionId }: { csrfToken: string; conditionId: number | string }) => {
  await wrapFetch(`/api/v1/recruitments/delete_save_conditions/${conditionId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ condition_id: conditionId }),
  });
};
